/**
 * One-time flag I/O for `@suite/tour`.
 *
 * Key shape: `suite.tour.<appId>.v1` → `TourRecord` JSON. localStorage is
 * wrapped in try/catch throughout: private-mode browsers, disabled storage
 * and Tauri webview quirks degrade to "the tour may replay", never to a crash.
 */
import type { TourRecord } from "./types";

const SCHEMA = "v1";

/** The storage key for one application's tour flag. */
export function tourStorageKey(appId: string): string {
  return `suite.tour.${appId}.${SCHEMA}`;
}

function safeParse(raw: string | null): TourRecord | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<TourRecord> | null;
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.completed !== "boolean") return null;
    return {
      completed: parsed.completed,
      version: typeof parsed.version === "number" ? parsed.version : 0,
      dismissedAt: typeof parsed.dismissedAt === "string" ? parsed.dismissedAt : undefined,
    };
  } catch {
    return null;
  }
}

/** The stored record, or null when the tour has never been answered. */
export function readTourRecord(appId: string): TourRecord | null {
  try {
    return safeParse(window.localStorage.getItem(tourStorageKey(appId)));
  } catch {
    return null;
  }
}

export function writeTourRecord(appId: string, record: TourRecord): void {
  try {
    window.localStorage.setItem(tourStorageKey(appId), JSON.stringify(record));
  } catch {
    // Storage refused — the tour simply becomes session-scoped for this user.
  }
}

/** Whether the user has already completed (or skipped) this tour. */
export function isTourCompleted(appId: string): boolean {
  return readTourRecord(appId) !== null;
}

/** Persist a finished tour. */
export function completeTour(appId: string, version: number): void {
  writeTourRecord(appId, { completed: true, version });
}

/** Persist a skipped tour — same silence on next launch, honest record. */
export function dismissTour(appId: string, version: number): void {
  writeTourRecord(appId, { completed: false, version, dismissedAt: new Date().toISOString() });
}

/**
 * One-time migration for applications that kept their own tour flag before
 * `@suite/tour` existed (kor's onboardingState, kur's `kur-tour-done`). When
 * any legacy key holds a truthy value, the shared record is written so those
 * users are not walked through the tour twice. Legacy keys are NOT removed —
 * the owning application decides when to retire them.
 */
export function migrateTourFlag(appId: string, legacyKeys: string[], version: number): boolean {
  if (readTourRecord(appId)) return false;
  try {
    for (const key of legacyKeys) {
      const raw = window.localStorage.getItem(key);
      if (raw === null) continue;
      if (raw === "1" || raw === "true" || raw === "done" || raw === "completed") {
        writeTourRecord(appId, { completed: true, version });
        return true;
      }
    }
  } catch {
    return false;
  }
  return false;
}
